import {locales,type Locale} from '@zzy/contracts';

type TutorialGuide<Id extends '01'|'02'|'03'|'04'> = Readonly<{
  id:Id;
  title:string;
  points:readonly [string,string,string];
  result:string;
}>;

export type TutorialCopy = Readonly<{
  title:string;
  description:string;
  timelineLabel:string;
  completeLabel:string;
  guides:readonly [TutorialGuide<'01'>,TutorialGuide<'02'>,TutorialGuide<'03'>,TutorialGuide<'04'>];
}>;

// tutorial-copy-v1: only the 24 tutorial UI strings. Chinese follows the approved
// tutorial; other languages follow 79ab967a:frontweb/src/locales (ko: ja.js column 3).
// User task prompts, installation scripts and shared dictionaries stay separate.
const copies:Readonly<Record<Locale,TutorialCopy>> = {
  "zh-CN": {
    "title": "4 步完成上手",
    "description": "安装助手、完成配置、选择技能，再把你的需求交给 Codex。",
    "timelineLabel": "Codex 使用教程完整步骤",
    "completeLabel": "完成后",
    "guides": [
      {
        "id": "01",
        "title": "下载并安装配置助手",
        "points": [
          "按系统下载 Windows 或 Apple 芯片 Mac 安装包",
          "按提示完成备份与安装",
          "安装后确认 Codex 能正常启动"
        ],
        "result": "配置助手安装完成"
      },
      {
        "id": "02",
        "title": "完成 Codex 配置",
        "points": [
          "在助手中完成账户连接",
          "选择当前任务要使用的模型",
          "确认连接状态后打开 Codex"
        ],
        "result": "Codex 已准备好接收任务"
      },
      {
        "id": "03",
        "title": "选择 Skill 模板",
        "points": [
          "从自媒体、办公、调研等分类选择",
          "复制模板说明到 Codex",
          "按提示生成第一个交付结果"
        ],
        "result": "快速开始实战任务"
      },
      {
        "id": "04",
        "title": "用话术开始第一个任务",
        "points": [
          "说明你想得到什么结果",
          "补充参考资料、用途和输出格式",
          "检查结果，并继续告诉 Codex 要调整什么"
        ],
        "result": "得到可以继续修改的任务成果"
      }
    ]
  },
  "en": {
    "title": "Get started in four steps",
    "description": "Install the assistant, complete setup, choose a skill and give Codex your task.",
    "timelineLabel": "Codex setup guide",
    "completeLabel": "Ready when",
    "guides": [
      {
        "id": "01",
        "title": "Download and install the assistant",
        "points": [
          "Choose the installer for Windows or Apple Silicon Mac",
          "Follow the installation instructions",
          "Check that Codex opens after installation"
        ],
        "result": "Assistant installed"
      },
      {
        "id": "02",
        "title": "Set up Codex",
        "points": [
          "Connect your account in the assistant",
          "Select a model for your task",
          "Check the connection, then open Codex"
        ],
        "result": "Codex is ready for your task"
      },
      {
        "id": "03",
        "title": "Choose a skill",
        "points": [
          "Browse content, office and research skills",
          "Send the installer and instructions to Codex",
          "Follow the project guide to create your first result"
        ],
        "result": "Start a practical task"
      },
      {
        "id": "04",
        "title": "Describe your first task",
        "points": [
          "Describe the result you need",
          "Add references, intended use and an output format",
          "Review the result and tell Codex what to adjust"
        ],
        "result": "Get a result you can keep refining"
      }
    ]
  },
  "ja": {
    "title": "4ステップで開始",
    "description": "アシスタントをインストールして設定とスキル選択を行い、要件をCodexに伝えます。",
    "timelineLabel": "Codexの完全ガイド",
    "completeLabel": "完了後",
    "guides": [
      {
        "id": "01",
        "title": "設定アシスタントをダウンロードしてインストール",
        "points": [
          "WindowsまたはApple Silicon Mac用を選択",
          "案内に従ってバックアップとインストールを完了",
          "インストール後にCodexの起動を確認"
        ],
        "result": "設定アシスタントのインストール完了"
      },
      {
        "id": "02",
        "title": "Codexの設定を完了",
        "points": [
          "アシスタントでアカウントを接続",
          "このタスクで使うモデルを選択",
          "接続状態を確認してCodexを開く"
        ],
        "result": "Codexでタスクを開始できます"
      },
      {
        "id": "03",
        "title": "Skillテンプレートを選択",
        "points": [
          "コンテンツ制作・業務・調査などから選択",
          "テンプレートの説明をCodexにコピー",
          "案内に従って最初の成果物を作成"
        ],
        "result": "実践タスクをすぐに開始"
      },
      {
        "id": "04",
        "title": "プロンプトで最初のタスクを開始",
        "points": [
          "希望する結果を説明",
          "参考資料・用途・出力形式を追加",
          "結果を確認し、修正点をCodexに伝える"
        ],
        "result": "引き続き編集できる成果物を取得"
      }
    ]
  },
  "ko": {
    "title": "4단계로 시작",
    "description": "도우미를 설치하고 설정 및 스킬 선택을 마친 뒤 Codex에 요구사항을 전달하세요.",
    "timelineLabel": "Codex 전체 사용 가이드",
    "completeLabel": "완료 후",
    "guides": [
      {
        "id": "01",
        "title": "설정 도우미 다운로드 및 설치",
        "points": [
          "Windows 또는 Apple Silicon Mac용 설치 파일 선택",
          "안내에 따라 백업 및 설치 완료",
          "설치 후 Codex 실행 확인"
        ],
        "result": "설정 도우미 설치 완료"
      },
      {
        "id": "02",
        "title": "Codex 설정 완료",
        "points": [
          "도우미에서 계정 연결 완료",
          "현재 작업에 사용할 모델 선택",
          "연결 상태 확인 후 Codex 열기"
        ],
        "result": "Codex 작업 준비 완료"
      },
      {
        "id": "03",
        "title": "Skill 템플릿 선택",
        "points": [
          "콘텐츠, 업무, 조사 등 분류에서 선택",
          "템플릿 설명을 Codex에 복사",
          "안내에 따라 첫 결과물 생성"
        ],
        "result": "실전 작업 빠르게 시작"
      },
      {
        "id": "04",
        "title": "프롬프트로 첫 작업 시작",
        "points": [
          "원하는 결과 설명",
          "참고 자료, 용도 및 출력 형식 추가",
          "결과를 확인하고 Codex에 수정할 사항 전달"
        ],
        "result": "계속 수정할 수 있는 작업 결과물 받기"
      }
    ]
  },
  "es": {
    "title": "Empieza en 4 pasos",
    "description": "Instala el asistente, configúralo, elige una Skill y asigna tu tarea a Codex.",
    "timelineLabel": "Guía de configuración de Codex",
    "completeLabel": "Listo cuando",
    "guides": [
      {
        "id": "01",
        "title": "Descargar e instalar el asistente",
        "points": [
          "Elige el instalador para Windows o Mac con Apple Silicon",
          "Sigue las instrucciones de instalación",
          "Comprueba que Codex se abre tras instalarlo"
        ],
        "result": "Asistente instalado"
      },
      {
        "id": "02",
        "title": "Configurar Codex",
        "points": [
          "Conecta tu cuenta en el asistente",
          "Selecciona un modelo para la tarea",
          "Comprueba la conexión y abre Codex"
        ],
        "result": "Codex está listo para recibir tu tarea"
      },
      {
        "id": "03",
        "title": "Elige una Skill",
        "points": [
          "Explora Skills de contenido, oficina e investigación",
          "Envía el instalador y las instrucciones a Codex",
          "Sigue la guía del proyecto para crear tu primer resultado"
        ],
        "result": "Empieza una tarea práctica"
      },
      {
        "id": "04",
        "title": "Describe tu primera tarea",
        "points": [
          "Describe el resultado que necesitas",
          "Añade referencias, uso previsto y formato de salida",
          "Revisa el resultado e indica a Codex qué ajustar"
        ],
        "result": "Obtén un resultado que puedas seguir mejorando"
      }
    ]
  },
  "pt-BR": {
    "title": "Comece em 4 etapas",
    "description": "Instale o assistente, configure, escolha uma Skill e passe sua tarefa ao Codex.",
    "timelineLabel": "Guia de configuração do Codex",
    "completeLabel": "Pronto quando",
    "guides": [
      {
        "id": "01",
        "title": "Baixar e instalar o assistente",
        "points": [
          "Escolha o instalador para Windows ou Mac com Apple Silicon",
          "Siga as instruções de instalação",
          "Confirme que o Codex abre após instalar"
        ],
        "result": "Assistente instalado"
      },
      {
        "id": "02",
        "title": "Configurar o Codex",
        "points": [
          "Conecte sua conta no assistente",
          "Selecione um modelo para a tarefa",
          "Confira a conexão e abra o Codex"
        ],
        "result": "O Codex está pronto para sua tarefa"
      },
      {
        "id": "03",
        "title": "Escolha uma Skill",
        "points": [
          "Explore Skills de conteúdo, escritório e pesquisa",
          "Envie o instalador e as instruções ao Codex",
          "Siga o guia do projeto para criar seu primeiro resultado"
        ],
        "result": "Comece uma tarefa prática"
      },
      {
        "id": "04",
        "title": "Descreva sua primeira tarefa",
        "points": [
          "Descreva o resultado desejado",
          "Adicione referências, finalidade e formato de saída",
          "Revise o resultado e diga ao Codex o que ajustar"
        ],
        "result": "Obtenha um resultado que pode continuar refinando"
      }
    ]
  },
  "vi": {
    "title": "Bắt đầu trong 4 bước",
    "description": "Cài trợ lý, hoàn tất cấu hình, chọn kỹ năng rồi giao nhu cầu cho Codex.",
    "timelineLabel": "Các bước hướng dẫn Codex đầy đủ",
    "completeLabel": "Sau khi hoàn tất",
    "guides": [
      {
        "id": "01",
        "title": "Tải và cài trợ lý thiết lập",
        "points": [
          "Chọn bộ cài Windows hoặc Mac Apple Silicon theo máy tính",
          "Làm theo hướng dẫn sao lưu và cài đặt",
          "Sau khi cài, xác nhận Codex mở bình thường"
        ],
        "result": "Đã cài trợ lý thiết lập"
      },
      {
        "id": "02",
        "title": "Hoàn tất thiết lập Codex",
        "points": [
          "Kết nối tài khoản trong trợ lý",
          "Chọn mô hình cho tác vụ hiện tại",
          "Kiểm tra kết nối rồi mở Codex"
        ],
        "result": "Codex đã sẵn sàng nhận tác vụ"
      },
      {
        "id": "03",
        "title": "Chọn mẫu Skill",
        "points": [
          "Chọn từ nội dung, văn phòng, nghiên cứu và các danh mục khác",
          "Sao chép hướng dẫn mẫu vào Codex",
          "Làm theo hướng dẫn để tạo kết quả đầu tiên"
        ],
        "result": "Bắt đầu tác vụ thực tế"
      },
      {
        "id": "04",
        "title": "Mô tả để bắt đầu tác vụ đầu tiên",
        "points": [
          "Nói rõ kết quả mong muốn",
          "Bổ sung tài liệu tham khảo, mục đích và định dạng đầu ra",
          "Kiểm tra kết quả và tiếp tục cho Codex biết cần sửa gì"
        ],
        "result": "Nhận kết quả có thể tiếp tục chỉnh sửa"
      }
    ]
  },
  "id": {
    "title": "Mulai dalam 4 langkah",
    "description": "Instal asisten, selesaikan penyiapan, pilih keterampilan, lalu berikan tugas ke Codex.",
    "timelineLabel": "Panduan lengkap Codex",
    "completeLabel": "Setelah selesai",
    "guides": [
      {
        "id": "01",
        "title": "Unduh dan instal asisten penyiapan",
        "points": [
          "Pilih penginstal Windows atau Mac Apple Silicon sesuai komputer",
          "Ikuti petunjuk pencadangan dan pemasangan",
          "Pastikan Codex dapat dibuka setelah pemasangan"
        ],
        "result": "Asisten penyiapan terpasang"
      },
      {
        "id": "02",
        "title": "Selesaikan penyiapan Codex",
        "points": [
          "Hubungkan akun di asisten",
          "Pilih model untuk tugas saat ini",
          "Periksa koneksi lalu buka Codex"
        ],
        "result": "Codex siap menerima tugas"
      },
      {
        "id": "03",
        "title": "Pilih templat Skill",
        "points": [
          "Pilih kategori konten, kantor, riset, dan lainnya",
          "Salin petunjuk templat ke Codex",
          "Ikuti petunjuk untuk membuat hasil pertama"
        ],
        "result": "Mulai tugas praktis"
      },
      {
        "id": "04",
        "title": "Jelaskan tugas pertama Anda",
        "points": [
          "Jelaskan hasil yang Anda inginkan",
          "Tambahkan referensi, tujuan, dan format keluaran",
          "Tinjau hasil dan beri tahu Codex apa yang perlu disesuaikan"
        ],
        "result": "Dapatkan hasil yang dapat terus disempurnakan"
      }
    ]
  },
  "th": {
    "title": "เริ่มใช้งานใน 4 ขั้นตอน",
    "description": "ติดตั้งผู้ช่วย ตั้งค่า เลือกทักษะ แล้วส่งความต้องการให้ Codex",
    "timelineLabel": "ขั้นตอนใช้งาน Codex แบบครบถ้วน",
    "completeLabel": "เมื่อเสร็จแล้ว",
    "guides": [
      {
        "id": "01",
        "title": "ดาวน์โหลดและติดตั้งผู้ช่วยตั้งค่า",
        "points": [
          "เลือกชุดติดตั้ง Windows หรือ Mac ชิป Apple ให้ตรงระบบ",
          "ทำตามคำแนะนำเพื่อสำรองและติดตั้ง",
          "ตรวจสอบว่า Codex เปิดได้ตามปกติหลังติดตั้ง"
        ],
        "result": "ติดตั้งผู้ช่วยตั้งค่าแล้ว"
      },
      {
        "id": "02",
        "title": "ตั้งค่า Codex ให้เสร็จ",
        "points": [
          "เชื่อมต่อบัญชีในผู้ช่วย",
          "เลือกโมเดลสำหรับงานปัจจุบัน",
          "ตรวจสอบสถานะการเชื่อมต่อแล้วเปิด Codex"
        ],
        "result": "Codex พร้อมรับงานแล้ว"
      },
      {
        "id": "03",
        "title": "เลือกเทมเพลต Skill",
        "points": [
          "เลือกจากหมวดคอนเทนต์ งานสำนักงาน วิจัย และอื่น ๆ",
          "คัดลอกคำแนะนำเทมเพลตไปยัง Codex",
          "ทำตามคำแนะนำเพื่อสร้างผลงานแรก"
        ],
        "result": "เริ่มงานจริงอย่างรวดเร็ว"
      },
      {
        "id": "04",
        "title": "อธิบายเพื่อเริ่มงานแรก",
        "points": [
          "บอกผลลัพธ์ที่คุณต้องการ",
          "เพิ่มข้อมูลอ้างอิง วัตถุประสงค์ และรูปแบบผลลัพธ์",
          "ตรวจผลงานแล้วบอก Codex ว่าต้องปรับอะไร"
        ],
        "result": "รับผลงานที่แก้ไขต่อได้"
      }
    ]
  },
  "fr": {
    "title": "Commencez en 4 étapes",
    "description": "Installez l’assistant, configurez-le, choisissez une Skill et confiez votre tâche à Codex.",
    "timelineLabel": "Guide de configuration de Codex",
    "completeLabel": "Prêt lorsque",
    "guides": [
      {
        "id": "01",
        "title": "Télécharger et installer l’assistant",
        "points": [
          "Choisissez le programme pour Windows ou Mac Apple Silicon",
          "Suivez les étapes d’installation",
          "Vérifiez que Codex s’ouvre après l’installation"
        ],
        "result": "Assistant installé"
      },
      {
        "id": "02",
        "title": "Configurer Codex",
        "points": [
          "Connectez votre compte dans l’assistant",
          "Choisissez un modèle pour votre tâche",
          "Vérifiez la connexion, puis ouvrez Codex"
        ],
        "result": "Codex est prêt à recevoir votre tâche"
      },
      {
        "id": "03",
        "title": "Choisir une Skill",
        "points": [
          "Parcourez les Skills de création, bureautique et recherche",
          "Envoyez le programme et les instructions à Codex",
          "Suivez le guide du projet pour créer votre premier résultat"
        ],
        "result": "Démarrer une tâche pratique"
      },
      {
        "id": "04",
        "title": "Décrire votre première tâche",
        "points": [
          "Décrivez le résultat souhaité",
          "Ajoutez des références, l’usage prévu et le format de sortie",
          "Examinez le résultat et indiquez à Codex les ajustements"
        ],
        "result": "Obtenez un résultat que vous pouvez continuer à améliorer"
      }
    ]
  },
  "de": {
    "title": "In 4 Schritten starten",
    "description": "Installieren Sie den Assistenten, richten Sie ihn ein, wählen Sie eine Skill und übergeben Sie Codex Ihre Aufgabe.",
    "timelineLabel": "Codex-Einrichtungsanleitung",
    "completeLabel": "Bereit, wenn",
    "guides": [
      {
        "id": "01",
        "title": "Assistenten herunterladen und installieren",
        "points": [
          "Installationspaket für Windows oder Apple-Silicon-Mac wählen",
          "Installationsanweisungen befolgen",
          "Prüfen, ob Codex nach der Installation startet"
        ],
        "result": "Assistent installiert"
      },
      {
        "id": "02",
        "title": "Codex einrichten",
        "points": [
          "Konto im Assistenten verbinden",
          "Modell für die Aufgabe auswählen",
          "Verbindung prüfen und Codex öffnen"
        ],
        "result": "Codex ist für Ihre Aufgabe bereit"
      },
      {
        "id": "03",
        "title": "Skill auswählen",
        "points": [
          "Skills für Inhalte, Büro und Recherche durchsuchen",
          "Installationspaket und Anleitung an Codex senden",
          "Mit der Projektanleitung das erste Ergebnis erstellen"
        ],
        "result": "Praktische Aufgabe starten"
      },
      {
        "id": "04",
        "title": "Erste Aufgabe beschreiben",
        "points": [
          "Gewünschtes Ergebnis beschreiben",
          "Referenzen, Verwendungszweck und Ausgabeformat ergänzen",
          "Ergebnis prüfen und Codex die gewünschten Anpassungen nennen"
        ],
        "result": "Ein weiter bearbeitbares Ergebnis erhalten"
      }
    ]
  },
  "ru": {
    "title": "Начало работы за 4 шага",
    "description": "Установите помощник, настройте его, выберите навык и передайте задачу Codex.",
    "timelineLabel": "Полное руководство по Codex",
    "completeLabel": "После выполнения",
    "guides": [
      {
        "id": "01",
        "title": "Скачать и установить помощник",
        "points": [
          "Выберите установщик Windows или Mac с чипом Apple",
          "Выполните резервное копирование и установку по инструкциям",
          "После установки убедитесь, что Codex запускается"
        ],
        "result": "Помощник установлен"
      },
      {
        "id": "02",
        "title": "Настроить Codex",
        "points": [
          "Подключите аккаунт в помощнике",
          "Выберите модель для текущей задачи",
          "Проверьте соединение и откройте Codex"
        ],
        "result": "Codex готов к задачам"
      },
      {
        "id": "03",
        "title": "Выбрать шаблон Skill",
        "points": [
          "Выберите навыки для контента, офиса или исследований",
          "Отправьте установщик и инструкции в Codex",
          "Создайте первый результат по инструкции проекта"
        ],
        "result": "Начать практическую задачу"
      },
      {
        "id": "04",
        "title": "Опишите первую задачу",
        "points": [
          "Укажите, какой результат нужен",
          "Добавьте материалы, назначение и формат результата",
          "Проверьте результат и сообщите Codex, что изменить"
        ],
        "result": "Получите результат, который можно дорабатывать"
      }
    ]
  }
};

// Freeze every returned collection and object, so one caller cannot alter a later
// call or another language. This module is pure content and does not select a URL.
for(const locale of locales){
  const copy=copies[locale];
  for(const guide of copy.guides){Object.freeze(guide.points);Object.freeze(guide)}
  Object.freeze(copy.guides);Object.freeze(copy);
}
Object.freeze(copies);

export function getTutorialCopy(locale:Locale):Readonly<TutorialCopy>{
  if(!locales.includes(locale))throw new RangeError('INVALID_TUTORIAL_LOCALE');
  return copies[locale];
}
